import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import useGlynt from './useGlynt';

type ParamTypes = {
  userId: string | undefined;
  limit: number;
  defaultPage: number;
};

export default function useDataExportGetByUser({ userId, limit = 100, defaultPage }: ParamTypes) {
  const [page, setPage] = useState(defaultPage || 0);
  const [isPolling, setIsPolling] = useState(false);
  const refetchIntervalMs = 5000;
  const glynt = useGlynt();

  const { data, isError, isPending, isFetching, refetch } = useQuery({
    queryKey: ['dataExort', 'getByUser', userId, page, limit],

    queryFn: async () =>
      (await glynt).dataExports.retrieveByUser({
        user: userId as string,
        limit,
        offset: page * limit,
      }),

    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60,
    enabled: !!userId,
    refetchInterval: isPolling ? refetchIntervalMs : false
  });

  useEffect(() => {
    if (data && data.results.some((exportItem) => exportItem.status === 'In Progress')) {
      setIsPolling(true);
    } else {
      setIsPolling(false);
    }
  }, [data]);

  const total = data ? Math.ceil(data.count / limit) : 1;

  const nextPage = () => {
    if (page < total - 1) setPage((page) => page + 1);
  };

  const previousPage = () => {
    if (page > 0) setPage((page) => page - 1);
  };

  return {
    data,
    error: isError,
    isPending,
    isLoading: isPending,
    loading: isPending,
    nextPage,
    previousPage,
    total,
    page,
    setPage,
    isFetching,
    refetch,
  };
}
